import OnboardingType from "../../../../../shared/onboarding/onboarding-type";
import { sortByName } from "../../../../../shared/utility";
import { ALIAS_PROP, CLEAR_DOWNLOAD_PROPS_OVERVIEW, DOWNLOAD_PROPS_OVERVIEW, FETCH_PROP_OVERVIEW, SAVE_ALIAS_PROP, SAVE_REMOVE_ALIAS_PROP, UNALIAS_PROP } from "./actions";
import { DELETE_MULTIPLE_PROPS } from "../../ducks/actions";

interface PropOverviewState {
  overview: any;
  loading: boolean;
  readOnly: boolean;
  onboardingSteps: OnboardingType[];
  download: any;
  progress: any;
  processingPropIds: string[];
}

export const OnboardingStep1ClassName = 'props-overview-onboarding-step-1';
export const OnboardingStep2ClassName = 'props-overview-onboarding-step-2';
export const OnboardingStep3ClassName = 'props-overview-onboarding-step-3';
export const OnboardingStep4ClassName = 'props-overview-onboarding-step-4';

const defaultState: PropOverviewState = {
  overview: {},
  loading: true,
  readOnly: false,
  download: null,
  progress: null,
  processingPropIds: [],
  onboardingSteps: [
    {
      target: '.' + OnboardingStep1ClassName,
      title: 'Props Acquired',
      content: `This shows the percentage of props acquired.`
    },
    {
      target: '.' + OnboardingStep2ClassName,
      title: 'Merge Props',
      content: `<p>Drag and drop props on top of each other to merge them.</p>
           <p>This is useful for combining duplicate props or aliasing props that are the same but have different names.</p>`
    }
  ]
};

const reducer = (state = defaultState, action: any = {}) => {
  switch (action.type) {
    case FETCH_PROP_OVERVIEW + "_PENDING": {
      return {
        ...state,
        loading: true,
        overview: null,
        download: null,
        errors: null
      };
    }

    case FETCH_PROP_OVERVIEW + "_FULFILLED": {
      return {
        ...state,
        loading: false,
        overview: action.payload.data,
        readOnly: action.payload.data.readOnly,
        errors: null
      };
    }


    case DOWNLOAD_PROPS_OVERVIEW + "_PENDING": {
      return {
        ...state,
        loading: false,
        progress: { progress: null, message: 'Queued for generating PDF' }
      };
    }

    case DOWNLOAD_PROPS_OVERVIEW + "_FULFILLED": {
      return state;
    }

    case DOWNLOAD_PROPS_OVERVIEW + "_COMPLETE": {
      return {
        ...state,
        loading: false,
        progress: null,
        download: { url: action.meta.url, fileName: action.meta.fileName, downloaded: false }
      };
    }

    case CLEAR_DOWNLOAD_PROPS_OVERVIEW: {
      var download = { ...state.download, downloaded: true }
      return {
        ...state,
        download: download
      };
    }

    case DOWNLOAD_PROPS_OVERVIEW + "_PROGRESS": {
      return {
        ...state,
        progress: action.meta.progress
      };
    }

    case DOWNLOAD_PROPS_OVERVIEW + "_ERRORED": {
      return {
        ...state,
        progress: null,
        errors: action.meta.errors
      };
    }

    case SAVE_ALIAS_PROP + "_PENDING": {
      const aliasProp = action.meta.aliasProp;
      const mainProp = action.meta.mainProp;
      return {
        ...state,
        processingPropIds: [
          ...state.processingPropIds,
          aliasProp.id,
          mainProp.id
        ]
      };
    }

    case SAVE_ALIAS_PROP + "_FULFILLED":
    case SAVE_ALIAS_PROP + "_REJECTED": {
      const aliasProp = action.meta.aliasProp;
      const mainProp = action.meta.mainProp;
      return {
        ...state,
        processingPropIds: state.processingPropIds.filter(
          id => id !== aliasProp.id && id !== mainProp.id
        )
      };
    }

    case SAVE_REMOVE_ALIAS_PROP + "_PENDING": {
      const aliasProp = action.meta.aliasProp;
      const mainProp = action.meta.mainProp;
      return {
        ...state,
        processingPropIds: [
          ...state.processingPropIds,
          aliasProp.id,
          mainProp.id
        ]
      };
    }

    case SAVE_REMOVE_ALIAS_PROP + "_FULFILLED":
    case SAVE_REMOVE_ALIAS_PROP + "_REJECTED": {
      const aliasProp = action.meta.aliasProp;
      const mainProp = action.meta.mainProp;
      return {
        ...state,
        processingPropIds: state.processingPropIds.filter(
          id => id !== aliasProp.id && id !== mainProp.id
        )
      };
    }

    case ALIAS_PROP: {
      const aliasProp = action.meta.aliasProp;
      const mainProp = action.meta.mainProp;
      let props = [...state.overview.props];
      const prop = props.find((c) => c.id === mainProp.id);
      const aliasPropIndex = props.findIndex(
        (c) => c.id === aliasProp.id
      );

      if (prop.aliases) {
        prop.aliases = [
          ...prop.aliases,
          aliasProp
        ];
      } else {
        prop.aliases = [aliasProp];
      }

      props[aliasPropIndex].aliases = [];

      props.splice(aliasPropIndex, 1);

      let overview = { ...state.overview, props };
      return {
        ...state,
        overview: overview
      };
    }

    case UNALIAS_PROP: {
      const aliasProp = action.meta.aliasProp;
      const mainProp = action.meta.mainProp;
      let props = [...state.overview.props];
      const prop = props.find((c) => c.id === mainProp.id);

      const aliasPropIndex = prop.aliases.findIndex(
        (a) => a.id === aliasProp.id
      );
      prop.aliases.splice(aliasPropIndex, 1);

      props.push(aliasProp);

      sortByName(props);

      let overview = { ...state.overview, props };
      return {
        ...state,
        overview: overview
      };
    }

    case DELETE_MULTIPLE_PROPS + "_PENDING": {
      return {
        ...state,
        loading: true,
        errors: null
      };
    }

    case DELETE_MULTIPLE_PROPS + "_FULFILLED": {
      const deletedPropIds = action.meta.propIds;
      let props = state.overview.props.filter(
        (prop) => !deletedPropIds.includes(prop.id)
      );

      let overview = { ...state.overview, props };
      return {
        ...state,
        overview: overview,
        loading: false
      };
    }

    case DELETE_MULTIPLE_PROPS + "_REJECTED": {
      return {
        ...state,
        errors: action.payload.response.data.errors,
        loading: false
      };
    }

    default:
      return state;
  }
};

export default reducer;
